import React, { useRef, useState } from 'react';
import {
    Button,
    Modal,
    Select,
    Upload,
    Table,
    Progress,
    Alert,
    Space,
    Typography,
    Tag,
    Tooltip,
} from 'antd';
import {
    UploadOutlined,
    TranslationOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { translateBatch, SUPPORTED_LANGUAGES } from '../../utils/translation';

const { Text, Title } = Typography;

export interface TemplatePush {
    title: string;
    body: string;
    delay_after_previous?: number;
    image_url?: string;
}

export interface PushTemplate {
    name?: string;
    lang?: string;
    pushes: TemplatePush[];
}

export interface LoadedPush {
    title: string;
    body: string;
    delay_after_previous?: number;
    image_url?: string;
}

interface PushTemplateLoaderProps {
    disabled?: boolean;
    onLoad: (pushes: LoadedPush[], templateName?: string, targetLang?: string) => void;

}

const LANG_OPTIONS = SUPPORTED_LANGUAGES.map((l) => ({
    value: l.code,
    label: `${l.abbr} — ${l.name}`,
}));

function parsePushTemplate(raw: unknown): PushTemplate {
    if (typeof raw !== 'object' || raw === null || !('pushes' in raw)) {
        throw new Error('Неверный формат: ожидается объект с полем "pushes"');
    }
    const tpl = raw as Record<string, unknown>;
    if (!Array.isArray(tpl.pushes) || tpl.pushes.length === 0) {
        throw new Error('Поле "pushes" должно быть непустым массивом');
    }
    tpl.pushes.forEach((p: unknown, i: number) => {
        if (typeof p !== 'object' || p === null) {
            throw new Error(`pushes[${i}]: ожидается объект`);
        }
        const push = p as Record<string, unknown>;
        if (!push.title || !push.body) {
            throw new Error(`pushes[${i}]: обязательные поля "title" и "body"`);
        }
    });
    return raw as PushTemplate;
}

type Step = 'upload' | 'preview' | 'translating' | 'done';

const PushTemplateLoader: React.FC<PushTemplateLoaderProps> = ({
    disabled = false,
    onLoad,
}) => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<Step>('upload');
    const [template, setTemplate] = useState<PushTemplate | null>(null);
    const [translatedPushes, setTranslatedPushes] = useState<LoadedPush[]>([]);
    const [targetLang, setTargetLang] = useState<string | undefined>(undefined);
    const [progress, setProgress] = useState(0);
    const [progressLabel, setProgressLabel] = useState('');
    const [error, setError] = useState<string | null>(null);
    const stopRef = useRef(false);
    const reset = () => {
        setStep('upload');
        setTemplate(null);
        setTranslatedPushes([]);
        setTargetLang(undefined);
        setProgress(0);
        setProgressLabel('');
        setError(null);
        stopRef.current = false;
    };

    const handleOpen = () => {
        reset();
        setOpen(true);
    };

    const handleClose = () => {
        stopRef.current = true;
        setOpen(false);
    };

    const handleFileRead = (file: UploadFile): boolean => {
        setError(null);
        const raw = file.originFileObj ?? (file as unknown as File);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parsed = JSON.parse(e.target?.result as string);
                const tpl = parsePushTemplate(parsed);
                setTemplate(tpl);
                setStep('preview');
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Ошибка парсинга JSON');
            }
        };
        reader.readAsText(raw);
        return false;
    };

    const handleConfirm = async () => {
        if (!template) return;
        stopRef.current = false;
        const srcLang = template.lang ?? 'en';
        const needTranslate = targetLang && targetLang !== srcLang;

        if (!needTranslate) {
            onLoad(template.pushes as LoadedPush[], template?.name, targetLang);
            setOpen(false);
            return;
        }
        setStep('translating');
        setProgress(0);
        const items = template.pushes.flatMap((p, i) => [
            { key: `${i}_title`, text: p.title },
            { key: `${i}_body`, text: p.body },
        ]);

        try {
            const translations = await translateBatch(
                items,
                {
                    from: srcLang,
                    to: targetLang!,
                    delay: 150,
                    onProgress: (done, total, key) => {
                        setProgress(Math.round((done / total) * 100));
                        setProgressLabel(key.endsWith('_title') ? 'Заголовок...' : 'Текст...');
                    },
                },
                stopRef,
            );

            if (stopRef.current) return;

            const result: LoadedPush[] = template.pushes.map((p, i) => ({
                ...p,
                title: translations[`${i}_title`] ?? p.title,
                body: translations[`${i}_body`] ?? p.body,
            }));

            setTranslatedPushes(result);
            setStep('done');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Ошибка перевода');
            setStep('preview');
        }
    };

    const handleApply = () => {
        onLoad(translatedPushes, template?.name, targetLang);
        setOpen(false);
    };

    const previewColumns: ColumnsType<TemplatePush | LoadedPush> = [
        {
            title: '№',
            width: 48,
            align: 'center',
            render: (_, __, i) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {i + 1}
                </Text>
            ),
        },
        {
            title: 'Заголовок / Текст',
            render: (_, record) => (
                <div>
                    <Text strong style={{ fontSize: 13 }}>
                        {record.title}
                    </Text>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        {record.body}
                    </div>
                </div>
            ),
        },
        {
            title: 'Задержка',
            width: 100,
            align: 'center',
            dataIndex: 'delay_after_previous',
            render: (d?: number) =>
                d ? (
                    <Tag color="blue">{d} мин</Tag>
                ) : (
                    <Tag color="default">Сразу</Tag>
                ),
        },
    ];

    const footer = () => {
        if (step === 'upload') {
            return (
                <Button onClick={handleClose}>Отмена</Button>
            );
        }
        if (step === 'preview') {
            return (
                <Space>
                    <Button onClick={reset}>← Назад</Button>
                    <Button type="primary" onClick={handleConfirm}>
                        {targetLang ? 'Перевести и применить' : 'Применить'}
                    </Button>
                </Space>
            );
        }
        if (step === 'translating') {
            return <Button danger onClick={handleClose}>Отменить</Button>;
        }
        return (
            <Space>
                <Button onClick={reset}>← Изменить</Button>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleApply}>
                    Применить {translatedPushes.length} пушей
                </Button>
            </Space>
        );
    };

    const content = () => {
        if (step === 'upload') {
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {error && <Alert type="error" message={error} showIcon />}

                    <div>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            JSON-файл должен содержать массив{' '}
                            <Text code>pushes</Text> с полями{' '}
                            <Text code>title</Text>, <Text code>body</Text>,{' '}
                            <Text code>delay_after_previous</Text> (мин).
                        </Text>
                        <pre
                            style={{
                                background: '#f5f5f5',
                                borderRadius: 6,
                                padding: '10px 14px',
                                fontSize: 12,
                                color: '#333',
                                marginBottom: 16,
                            }}
                        >
                            {`{
  "lang": "en",
  "pushes": [
    { "title": "Welcome!", "body": "Thanks for joining", "delay_after_previous": 0 },
    { "title": "Hot offer 🔥", "body": "Get 50% off today", "delay_after_previous": 60 }
  ]
}`}
                        </pre>

                        <Upload
                            accept=".json"
                            showUploadList={false}
                            beforeUpload={handleFileRead}
                        >
                            <Button icon={<UploadOutlined />} size="large" block>
                                Выбрать JSON-файл
                            </Button>
                        </Upload>
                    </div>

                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            <TranslationOutlined /> Язык перевода (необязательно)
                        </Text>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Не переводить (использовать оригинал)"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={LANG_OPTIONS}
                            value={targetLang}
                            onChange={setTargetLang}
                        />
                    </div>
                </Space>
            );
        }

        if (step === 'preview') {
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {error && <Alert type="error" message={error} showIcon />}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={5} style={{ margin: 0 }}>
                            <FileTextOutlined /> {template?.name ?? 'Шаблон'} —{' '}
                            <Text type="secondary">{template?.pushes.length} уведомлений</Text>
                        </Title>
                        {targetLang && (
                            <Tag color="blue" icon={<TranslationOutlined />}>
                                → {SUPPORTED_LANGUAGES.find((l) => l.code === targetLang)?.abbr ?? targetLang}
                            </Tag>
                        )}
                    </div>
                    <Table
                        dataSource={template?.pushes ?? []}
                        columns={previewColumns}
                        pagination={false}
                        size="small"
                        rowKey={(_, i) => String(i)}
                    />
                </Space>
            );
        }

        if (step === 'translating') {
            return (
                <Space direction="vertical" style={{ width: '100%', padding: '24px 0' }} size="large">
                    <div style={{ textAlign: 'center' }}>
                        <TranslationOutlined style={{ fontSize: 32, color: '#1677ff', marginBottom: 12 }} />
                        <Title level={4} style={{ margin: 0 }}>Перевод...</Title>
                        <Text type="secondary">{progressLabel}</Text>
                    </div>
                    <Progress percent={progress} status="active" />
                </Space>
            );
        }
        return (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Alert
                    type="success"
                    icon={<CheckCircleOutlined />}
                    showIcon
                    message={`Перевод завершён — ${translatedPushes.length} уведомлений готовы к импорту`}
                />
                <Table
                    dataSource={translatedPushes}
                    columns={previewColumns}
                    pagination={false}
                    size="small"
                    rowKey={(_, i) => String(i)}
                />
            </Space>
        );
    };

    return (
        <>
            <Tooltip
                title={disabled ? 'Сначала выберите приложение' : 'Загрузить список пушей из JSON-шаблона'}
            >
                <Button
                    icon={<FileTextOutlined />}
                    disabled={disabled}
                    onClick={handleOpen}
                >
                    Загрузить из шаблона
                </Button>
            </Tooltip>

            <Modal
                title="Импорт push-уведомлений из шаблона"
                open={open}
                onCancel={handleClose}
                footer={footer()}
                width={680}
                destroyOnClose
            >
                {content()}
            </Modal>
        </>
    );
};

export default PushTemplateLoader;